import { NgIf } from '@angular/common';
import { Component, inject, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormHelper } from '@common/form-helper';
import { CardComponent } from '@components/card/card.component';
import { SelectedCenterCardComponent } from '@components/selected-center-card/selected-center-card.component';
import { environment } from '@environments/environment';
import { Map, NgFindalabModule, Result, ResultService } from '@Medology/ng-findalab';
import { Address } from '@models/address';
import { ConsultationRequestPharmacy } from '@models/consultation-request/consultation-request-pharmacy';
import { MapsLoadService } from '@services/maps-load.service';
import { OrderService } from '@services/order.service';

@Component({
  selector: 'app-consultation-request-choose-a-pharmacy',
  templateUrl: './choose-a-pharmacy.component.html',
  styleUrls: ['../../test-center/test-center.component.scss', './choose-a-pharmacy.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [CardComponent, NgIf, NgFindalabModule, SelectedCenterCardComponent],
})
export class ChooseAPharmacyComponent extends FormHelper implements OnInit {
  @Input() public apiUrl: string;
  @Input() public pharmacyForm: FormGroup;
  @Input() public zipcode: string = null;
  @Input() public pharmacy: ConsultationRequestPharmacy;

  public map: Map = null;
  public changingLab: boolean = false;
  public selectedPharmacy: Result | ConsultationRequestPharmacy;
  public states = Address.statesUS;

  private resultService = inject(ResultService);
  private mapsLoadService = inject(MapsLoadService);
  private orderService = inject(OrderService);

  /**
   * Configures the map.
   */
  public ngOnInit(): void {
    this.orderService.labResultSubscription.unsubscribe();
    this.mapsLoadService.getGoogleMapApi(environment.googleMapApiKey).subscribe(() => {
      this.map = new Map(this.getMapConfig());
    });

    this.initializeSubscriptions();
    this.preSelectPharmacy();
  }

  /**
   * Gets map config object.
   */
  private getMapConfig(): Map {
    let map = {
      searchUrl: {
        hostname: `${environment.analyteCareApi}/api/v1`,
        path: '/pharmacies/labs/nearPostalCode',
      },
      apiKey: environment.googleMapApiKey,
      testing: !environment.production,
      searchFunction: {
        onlyNetworks: [],
        onlyStates: null,
        filterByOrder: '',
      },
      inputGroup: {
        container: 'field has-addons',
        field: 'input control',
        button: 'control',
      },
      lab: {
        phone: {
          show: true,
          text: 'Phone:',
        },
        button: {
          show: true,
          text: environment.ngFindalab.labButtonText,
          class: `${environment.styles.chooseAPharmacy.labButtonClass} button animated-right`,
        },
        icon: {
          show: true,
          class: 'fa fa-arrow-right ml-3 is-pulled-right',
        },
        recommended: {},
      },
      search: {
        title: environment.ngFindalab.choosePharmacySearchTitle,
        description: environment.ngFindalab.pharmacySearchDescription,
        button: {
          class: `button ${environment.styles.chooseAPharmacy.searchButtonClass}`,
          loadingText: '',
          text: environment.ngFindalab.searchButtonText,
          icon: {
            show: environment.ngFindalab.showSearchIcon,
            class: 'fa fa-search',
            loadingClass: 'fa fa-spinner fa-spin',
          },
        },
        placeholder: 'Enter your zip',
        inputType: 'tel',
        showDescription: true,
        messageNoResults:
          'Oops! Sorry, we could not find any pharmacies near that location. ' +
          'Please try your search again with a different or less specific address.',
      },
      userLocation: {
        showOption: true,
        icon: 'fa fa-map-marker',
        msg: environment.ngFindalab.locateMsg,
        loading: {
          icon: 'fa fa-spin fa-spinner',
          msg: 'Searching current location...',
        },
      },
      mobileBreakpoint: 848,
      googleMaps: {
        defaultLat: 39.97712,
        defaultLong: -99.587403,
        geoCoder: null,
        infoWindow: null,
        initialZoom: 4,
        map: null,
        markers: [],
        resultsZoom: 12,
        labMarkerFillColor: environment.styles.chooseAPharmacy.googleMapsLabMarkerFillColor,
        recommendedMarkerFillColor: environment.styles.chooseAPharmacy.googleMapsRecommendedMarkerFillColor,
        markerHoverFillColor: environment.styles.chooseAPharmacy.googleMapsMarkerHoverFillColor,
      },
      postalCode: this.zipcode,
      mapId: environment.ngFindalab.findalabMapId,
    };

    return map as Map;
  }

  /**
   * Initialize subscriptions
   */
  private initializeSubscriptions(): void {
    this.resultService.resultSelected$.subscribe((result: Result) => {
      this.selectPharmacy(result);
    });
  }

  /**
   * Pre-select the pharmacy if it exists
   */
  private preSelectPharmacy(): void {
    if (this.pharmacy) {
      this.selectPharmacy(this.pharmacy);
    }
  }

  /**
   * Select a provide pharmacy
   *
   * @param {Result | ConsultationRequestPharmacy} pharmacy The pharmacy to select
   */
  private selectPharmacy(pharmacy: Result | ConsultationRequestPharmacy): void {
    this.changingLab = false;
    this.selectedPharmacy = pharmacy;
    this.pharmacyForm.patchValue({
      address: pharmacy.address,
      city: pharmacy.city,
      id: pharmacy.id,
      title: pharmacy.title,
      name: pharmacy.title,
      zip_code: pharmacy.zip_code,
      phone: pharmacy.phone,
      state: pharmacy.state,
    });
  }
}
